import { InvalidUrlError } from '@execonline-inc/url';
import { HttpError } from 'ajaxian';
import { Maybe, nothing } from 'maybeasy';
import { SetStateAction } from 'react';
import { AppyError } from '../../../../../Appy';
import { Resource } from '../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../Translations';
import { ToString } from '../../../../Options/Types';

export const emptyOption: FilterOption = {
  links: [],
  payload: {
    value: '',
    label: '',
    type: nothing<OptionType>(),
    email: nothing<string>(),
    participantCount: nothing<number>(),
    initials: nothing<string>(),
  },
};

export type NewState = SetStateAction<State>;
export type FilterError = HttpError | InvalidUrlError | AppyError | InitialValueMissing;

interface InitialValueMissing {
  kind: 'initial-value-missing';
}

export type OptionType = 'group' | 'participant';

export interface ParticipantAndGroupOption<T extends ToString> {
  value: T;
  label: string;
  type: Maybe<OptionType>;
  email: Maybe<string>;
  participantCount: Maybe<number>;
  initials: Maybe<string>;
}

export type ParticipantAndGroupOptionResource = Resource<ParticipantAndGroupOption<string>>;
export type FilterOption = ParticipantAndGroupOptionResource;
export type FilterOptions = FilterOption[];
export type FilterOptionsResource = Resource<FilterOptions>;

export interface Loading {
  kind: 'loading';
  initialValue: Maybe<FilterOption>;
}

export interface Ready {
  kind: 'ready';
}

export interface Filtering {
  kind: 'filtering';
  filterBy: string;
}

export interface Filtered {
  kind: 'filtered';
  filterBy: string;
  optionsResource: FilterOptionsResource;
  selectedValue: Maybe<FilterOption>;
}

export interface OptionSelected {
  kind: 'option-selected';
  optionsResource: FilterOptionsResource;
  selectedValue: FilterOption;
}

export interface Error {
  kind: 'error';
  message: TPlainTextKey;
}

export type State = Loading | Ready | Filtering | Filtered | OptionSelected | Error;

export const loading = (initialValue: Maybe<FilterOption>): Loading => ({
  kind: 'loading',
  initialValue,
});

export const ready = (): Ready => ({
  kind: 'ready',
});

export const filtering = (filterBy: string): Filtering => ({
  kind: 'filtering',
  filterBy,
});

export const filtered = (
  optionsResource: FilterOptionsResource,
  state: Loading | Filtering,
): Filtered => {
  const staticValues: Pick<Filtered, 'kind' | 'optionsResource'> = {
    kind: 'filtered',
    optionsResource,
  };

  switch (state.kind) {
    case 'loading':
      return {
        ...staticValues,
        filterBy: state.initialValue.map((iv) => iv.payload.value).getOrElseValue(''),
        selectedValue: state.initialValue,
      };
    case 'filtering':
      return {
        ...staticValues,
        filterBy: state.filterBy,
        selectedValue: nothing(),
      };
  }
};

export const optionSelected = (
  selectedValue: FilterOption,
  optionsResource: FilterOptionsResource,
): OptionSelected => ({
  kind: 'option-selected',
  optionsResource,
  selectedValue,
});

export const error = (message: TPlainTextKey): Error => ({
  kind: 'error',
  message,
});

interface InitialValueMissing {
  kind: 'initial-value-missing';
}

export const initialValueMissing = (): InitialValueMissing => ({
  kind: 'initial-value-missing',
});
